// Vollea — Super Admin Overview (stats grid + action rows + notification bell)
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoChevronForward, IoTicketOutline, IoChatbubblesOutline, IoPeopleOutline, IoSettingsOutline, IoBusinessOutline } from "react-icons/io5";
import { Card, StatBox } from "../../components/ui-vollea";
import NotificationBell from "../../components/NotificationBell";
import { api } from "../../lib/api";
import { C, SP, R } from "../../lib/theme";

export default function AdminStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});

  useEffect(() => {
    const pull = () => api.get("/admin/stats").then(setStats).catch(() => {});
    pull();
    // Poll every 30s so the feedback / pending-clubs counters refresh without a reload.
    const t = setInterval(pull, 30000);
    return () => clearInterval(t);
  }, []);

  const newFeedback = Number(stats.feedback_new || 0);
  const pendingClubs = Number(stats.pending_clubs || 0);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP.md }}>
        <button onClick={() => navigate("/dashboard")} data-testid="admin-back" style={{ color: C.text, background: "transparent", border: "none", cursor: "pointer" }}>
          <IoChevronBack size={26} />
        </button>
        <NotificationBell testid="admin-notification-bell" />
      </div>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>Super Admin</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4 }}>Platform overview</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: SP.md, marginTop: SP.xl }}>
        <StatBox value={stats.clubs ?? 0} label="Clubs" accent testid="stat-clubs" />
        <StatBox value={stats.leagues ?? 0} label="Leagues" accent testid="stat-leagues" />
        <StatBox value={stats.users ?? 0} label="Users" accent testid="stat-users" />
        <StatBox value={stats.matches ?? 0} label="Matches" accent testid="stat-matches" />
      </div>

      <div style={{ marginTop: SP.xl, display: "flex", flexDirection: "column", gap: SP.md }}>
        <ActionRow icon={<IoBusinessOutline size={22} color={C.lime} />} title="Clubs" subtitle={`${pendingClubs} pending approval`} badge={pendingClubs} onClick={() => navigate("/admin/clubs")} testid="admin-link-clubs" />
        <ActionRow icon={<IoTicketOutline size={22} color={C.lime} />} title="Beta Invites" subtitle="Create & manage invite codes" onClick={() => navigate("/admin/invites")} testid="admin-link-invites" />
        <ActionRow
          icon={<IoChatbubblesOutline size={22} color={C.lime} />}
          title="Beta Feedback"
          subtitle={newFeedback === 0 ? "All caught up" : `${newFeedback} new message${newFeedback === 1 ? "" : "s"}`}
          badge={newFeedback}
          onClick={() => navigate("/admin/feedback")}
          testid="admin-link-feedback"
        />
        <ActionRow icon={<IoPeopleOutline size={22} color={C.lime} />} title="Users" subtitle="Browse the user list" onClick={() => navigate("/admin/users")} testid="admin-link-users" />
        <ActionRow icon={<IoSettingsOutline size={22} color={C.lime} />} title="Settings" subtitle="Beta gating & club approvals" onClick={() => navigate("/admin/settings")} testid="admin-link-settings" />
      </div>
    </div>
  );
}

function ActionRow({ icon, title, subtitle, onClick, testid, badge }) {
  const showBadge = Number(badge || 0) > 0;
  return (
    <Card onClick={onClick} testid={testid} style={showBadge ? { border: `1px solid ${C.lime}` } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
        <div style={{ width: 44, height: 44, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 16, display: "inline-flex", alignItems: "center", gap: SP.sm }}>
            {title}
            {showBadge && (
              <span
                data-testid={`${testid}-badge`}
                style={{
                  background: C.lime, color: C.onLime,
                  fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                  borderRadius: R.pill, padding: "2px 8px", lineHeight: 1.4,
                }}
              >
                {badge > 99 ? "99+" : badge} new
              </span>
            )}
          </div>
          <div style={{ color: C.textMuted, fontSize: 13 }}>{subtitle}</div>
        </div>
        <IoChevronForward size={20} color={C.textDim} />
      </div>
    </Card>
  );
}
